import { Controller } from "@hotwired/stimulus"
import { savePlan } from "plan_storage"

// Theme picker — backs the toolbar's theme button. Maintains
// plan.meta.theme in localStorage so /editor (iframe), the native
// editor, and the preview share the same theme. Drops a small
// floating menu of swatches when the toolbar button is clicked.

const STORAGE_KEY = "pcp_plan_data"

const THEMES = {
  celebration: { name: "Celebration & Dreams", primary: "#D946EF" },
  sunshine:    { name: "Sunshine & Joy",       primary: "#F59E0B" },
  mountain:    { name: "Mountain Summit",      primary: "#059669" },
  ocean:       { name: "Ocean Flow",           primary: "#0EA5E9" },
  blue:        { name: "Artistic Drawing",     primary: "#2563EB" },
  contrast:    { name: "Train Journey",        primary: "#B91C1C" },
  carrace:     { name: "Car Race",             primary: "#DC2626" },
  forest:      { name: "Forest Hike",          primary: "#166534" },
  sunset:      { name: "Sunset Vibes",         primary: "#C2410C" },
  berry:       { name: "Wild Berry",           primary: "#BE185D" },
  lavender:    { name: "Calm Lavender",        primary: "#7C3AED" },
  midnight:    { name: "Midnight Star",        primary: "#1E3A8A" },
  mint:        { name: "Fresh Mint",           primary: "#0D9488" },
  earth:       { name: "Earth & Clay",         primary: "#A16207" },
  royal:       { name: "Royal Gold",           primary: "#B45309" },
  slate:       { name: "Slate Grey",           primary: "#475569" }
}

export default class extends Controller {
  static targets = ["menu", "button"]

  connect() {
    this.plan = this._load()
    if (!this.plan.meta) this.plan.meta = {}
    this._renderMenu()
    this._reflect()
    this._onDoc = (e) => {
      if (!this.element.contains(e.target)) this._close()
    }
  }

  toggle(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (this.menuTarget.classList.contains("is-open")) {
      this._close()
    } else {
      this._open()
    }
  }

  pick(event) {
    const key = event.currentTarget.dataset.themeKey
    if (!key || !THEMES[key]) return
    this.plan.meta.theme = key
    this._save()
    this._reflect()
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "meta.theme", value: key, plan: this.plan }
    }))
    this._close()
  }

  _renderMenu() {
    this.menuTarget.innerHTML = Object.entries(THEMES).map(([key, t]) => `
      <button type="button"
              class="en-theme-item"
              data-theme-key="${key}"
              data-action="theme-picker#pick"
              title="${t.name}">
        <span class="en-theme-swatch" style="background:${t.primary}"></span>
        <span class="en-theme-name">${t.name}</span>
      </button>`).join("")
  }

  _reflect() {
    const key = this.plan.meta?.theme || "celebration"
    const t = THEMES[key] || THEMES.celebration
    // Update the button swatch + tooltip.
    if (this.hasButtonTarget) {
      const swatch = this.buttonTarget.querySelector(".en-theme-swatch")
      if (swatch) swatch.style.background = t.primary
      this.buttonTarget.title = `Theme: ${t.name}`
    }
    // Update menu's active item.
    this.menuTarget.querySelectorAll(".en-theme-item").forEach(el => {
      el.classList.toggle("is-active", el.dataset.themeKey === key)
    })
  }

  _open() {
    this.menuTarget.classList.add("is-open")
    document.addEventListener("click", this._onDoc, true)
  }
  _close() {
    this.menuTarget.classList.remove("is-open")
    document.removeEventListener("click", this._onDoc, true)
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
  _save() {
    savePlan(this.plan)
  }
}
